import React, { useState } from 'react';
import { FileText, CheckCircle2, ChevronDown, User, Building2, CreditCard, Bot } from 'lucide-react';

interface FormData {
  firstName: string;
  lastName: string;
  personalNumber: string;
  email: string;
  phone: string;
  address: string;
  projectTitle: string;
  projectType: string;
  projectDescription: string;
  requestedAmount: string;
  timeline: string;
  bankAccount: string;
  swishNumber: string;
  clearingNumber: string;
}

interface FormSection {
  id: string;
  title: string;
  icon: JSX.Element;
  fields: {
    id: keyof FormData;
    label: string;
    type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'number';
    placeholder: string;
    options?: string[];
  }[];
}

export const ApplicationTemplates: React.FC = () => {
  const [activeTemplate, setActiveTemplate] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    personalNumber: '',
    email: '',
    phone: '',
    address: '',
    projectTitle: '',
    projectType: '',
    projectDescription: '',
    requestedAmount: '',
    timeline: '',
    bankAccount: '',
    swishNumber: '',
    clearingNumber: ''
  });

  const templates = [
    {
      id: 'research',
      title: 'Forskningsanslag',
      description: 'För akademisk forskning och vetenskapliga projekt',
      category: 'Forskning',
      requirements: ['Doktorsexamen', 'Forskningsplan', 'Budget']
    },
    {
      id: 'culture',
      title: 'Kulturprojekt',
      description: 'För konstnärliga och kulturella initiativ',
      category: 'Kultur',
      requirements: ['Portfolio', 'Projektbeskrivning', 'Tidplan']
    },
    {
      id: 'education',
      title: 'Utbildningsstipendium',
      description: 'För studier och utbildningsrelaterade projekt',
      category: 'Utbildning',
      requirements: ['Studieintyg', 'Motivering', 'Studieplan']
    },
    {
      id: 'innovation',
      title: 'Innovationsprojekt',
      description: 'För tekniska och innovativa projekt',
      category: 'Innovation',
      requirements: ['Affärsplan', 'Prototypbeskrivning', 'Marknadsanalys']
    }
  ];

  const formSections: FormSection[] = [
    {
      id: 'personal',
      title: 'Personuppgifter',
      icon: <User className="w-5 h-5" />,
      fields: [
        { id: 'firstName', label: 'Förnamn', type: 'text', placeholder: 'Ditt förnamn' },
        { id: 'lastName', label: 'Efternamn', type: 'text', placeholder: 'Ditt efternamn' },
        { id: 'personalNumber', label: 'Personnummer', type: 'text', placeholder: 'ÅÅÅÅMMDD-XXXX' },
        { id: 'email', label: 'E-post', type: 'email', placeholder: 'din@email.se' },
        { id: 'phone', label: 'Telefon', type: 'tel', placeholder: '07X-XXX XX XX' },
        { id: 'address', label: 'Adress', type: 'text', placeholder: 'Din fullständiga adress' }
      ]
    },
    {
      id: 'project',
      title: 'Projektinformation',
      icon: <Building2 className="w-5 h-5" />,
      fields: [
        { id: 'projectTitle', label: 'Projekttitel', type: 'text', placeholder: 'Projektets namn' },
        { 
          id: 'projectType', 
          label: 'Projekttyp', 
          type: 'select', 
          placeholder: 'Välj projekttyp',
          options: ['Forskning', 'Kultur', 'Utbildning', 'Innovation', 'Övrigt']
        },
        { 
          id: 'projectDescription', 
          label: 'Projektbeskrivning', 
          type: 'textarea', 
          placeholder: 'Beskriv ditt projekt...'
        },
        { 
          id: 'requestedAmount', 
          label: 'Sökt belopp (SEK)', 
          type: 'number', 
          placeholder: '100000'
        },
        { 
          id: 'timeline', 
          label: 'Tidsplan', 
          type: 'text', 
          placeholder: 'ex. Jan 2024 - Dec 2024'
        }
      ]
    },
    {
      id: 'financial',
      title: 'Ekonomisk Information',
      icon: <CreditCard className="w-5 h-5" />,
      fields: [
        { 
          id: 'bankAccount', 
          label: 'Bankkonto', 
          type: 'text', 
          placeholder: 'Kontonummer'
        },
        { 
          id: 'clearingNumber', 
          label: 'Clearingnummer', 
          type: 'text', 
          placeholder: 'xxxx'
        },
        { 
          id: 'swishNumber', 
          label: 'Swish-nummer', 
          type: 'tel', 
          placeholder: '07XXXXXXXX'
        }
      ]
    }
  ];

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  return (
    <div className="space-y-8">
      {!showForm ? (
        <>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Välj Ansökningsmall
            </h2>
            <p className="text-gray-600">
              Välj den mall som bäst passar ditt ändamål
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {templates.map((template) => (
              <button
                key={template.id}
                onClick={() => {
                  setActiveTemplate(template.id);
                  setShowForm(true);
                }}
                className="text-left bg-white rounded-xl border border-gray-200 p-6 
                         hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-shrink-0 p-2 bg-rose-100 rounded-lg">
                    <FileText className="w-6 h-6 text-rose-600" />
                  </div>
                  <span className="text-sm font-medium text-rose-600">
                    {template.category}
                  </span>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {template.title}
                </h3>

                <p className="text-gray-600 mb-4">
                  {template.description}
                </p>

                <div className="space-y-2">
                  {template.requirements.map((req, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-gray-500">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>{req}</span>
                    </div>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {templates.find(t => t.id === activeTemplate)?.title}
            </h2>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              Tillbaka till mallar
            </button>
          </div>

          {formSections.map((section) => (
            <div key={section.id} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-6">
                {section.icon}
                <h3 className="text-lg font-semibold text-gray-900">
                  {section.title}
                </h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {section.fields.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <label htmlFor={field.id} className="block text-sm font-medium text-gray-700">
                      {field.label}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        id={field.id}
                        value={formData[field.id]}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full h-32 rounded-lg border-gray-300 shadow-sm 
                                 focus:border-rose-500 focus:ring-rose-500"
                      />
                    ) : field.type === 'select' ? (
                      <select
                        id={field.id}
                        value={formData[field.id]}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        className="w-full rounded-lg border-gray-300 shadow-sm 
                                 focus:border-rose-500 focus:ring-rose-500"
                      >
                        <option value="">{field.placeholder}</option>
                        {field.options?.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        id={field.id}
                        value={formData[field.id]}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full rounded-lg border-gray-300 shadow-sm 
                                 focus:border-rose-500 focus:ring-rose-500"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 
                       transition-colors flex items-center gap-2"
            >
              Skicka ansökan
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ApplicationTemplates;